import { useEffect, useState } from 'react';
import convertItemToUpdate from 'services/utils/convertItemToUpdate';
import { Endpoint } from '../base/endpoints';
import { useBaseService } from '../base/useBaseService';
import getItemName from '../utils/getItemName';
import type { Item } from './item.type';

export default function useItems(setId: string, initialItemId?: string) {
  const [items, setItems] = useState<Item[]>([]);
  const [isLoadingNewItem, setIsLoadingNewItem] = useState(false);

  // the create method messes with the initial creations of items when it's creating the first item
  // so this is to differentiate between tht two
  const [initialised, setInitialised] = useState(false);

  const service = useBaseService(
    {
      queryKey: ['items', setId],
      endpoints: {
        get: Endpoint.getItems,
        create: Endpoint.createItem,
        delete: Endpoint.deleteItem,
        order: Endpoint.orderItems,
        update: Endpoint.updateItem,
        clone: Endpoint.cloneItem,
      },
      fetchAllPages: true,
    },
    setId
  );
  const { isLoading, data } = service;

  const itemMethods = (item) => ({
    name: getItemName(item),
    delete: () => {
      service.delete({ pathParams: { id: item.id } });
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    },
    select: () => {
      setItems((prev) =>
        prev.map((i) => ({
          ...i,
          selected: i.id === item.id,
        }))
      );
    },
    duplicate: async () => {
      setIsLoadingNewItem(true);
      const newItem = await service.clone({ pathParams: { id: item.id } });
      setItems((prev) => [...prev, { ...item, id: newItem.id, ...itemMethods(newItem), selected: false }]);
      setIsLoadingNewItem(false);
    },
  });

  useEffect(() => {
    if (data?.length > 0 && !initialised) {
      setInitialised(true);
      const hasTargetItem = initialItemId && data.some((item) => item.id === initialItemId);
      setItems(
        data.map((item, idx) => ({
          ...item,
          ...itemMethods(item),
          selected: hasTargetItem ? item.id === initialItemId : idx === 0,
        }))
      );
    }
  }, [data]);

  const reorder = (newOrder: string[]) => {
    service.order({ data: { ordering: newOrder } });
    setItems((prev) => newOrder.map((id) => prev.find((item) => item.id === id) as Item));
  };

  const create = async (templateType, answerType, instructionalContentSubtype, createItemCallback) => {
    setInitialised(true);
    setIsLoadingNewItem(true);
    const newItem = await service.create({ data: { attributes: { position: items.length } } });

    await service.update({
      data: convertItemToUpdate(newItem, templateType, answerType, instructionalContentSubtype),
      pathParams: { id: newItem.id },
    });

    const { emptyFacet, emptyAnnotation } = await createItemCallback(templateType, newItem.id);

    // some templates seem to have different type names on creation than they do on load
    const tempateTypeAliases =
      {
        free_response: 'free_responses',
      }[templateType] || templateType;

    newItem.templateType = tempateTypeAliases;
    newItem.answerType = answerType;
    newItem.icSubtype = instructionalContentSubtype;
    if (emptyAnnotation) {
      newItem.annotations.push(emptyAnnotation);
    }

    // These need the first facet id to get the distractors on first load
    const facets = [
      'question_and_answers',
      'survey_question_and_answers',
      'associations',
      'vocabulary',
      'free_response',
    ].includes(templateType)
      ? [emptyFacet]
      : [];

    setItems((prev) => [...prev, { ...newItem, ...itemMethods(newItem), facets }]);
    setIsLoadingNewItem(false);
    return newItem;
  };

  return { service, items, setItems, reorder, create, isLoading, isLoadingNewItem };
}
